import { Article } from '../types';
import { API_BASE_URL, METADATA_URL } from '../constants/api';

// HTMLからメタデータを抽出する関数
const extractMetadata = (html: string): Partial<Article> => {
  const metadata: Partial<Article> = {};
  
  // HTMLタグを除去する関数
  const stripHtmlTags = (html: string) => {
    return html.replace(/<[^>]*>/g, '');
  };
  
  // タイトルを抽出（h1タグから）
  const titleMatch = html.match(/<h1[^>]*>(.*?)<\/h1>/i);
  if (titleMatch && titleMatch[1]) {
    // HTMLタグを除去してタイトルを設定
    metadata.title = stripHtmlTags(titleMatch[1].trim());
  }
  
  // メタタグからの抽出
  const slugMatch = html.match(/<meta\s+name="slug"\s+content="([^"]+)"\s*\/?>/i);
  if (slugMatch && slugMatch[1]) {
    metadata.slug = slugMatch[1];
  }
  
  const dateMatch = html.match(/<meta\s+name="date"\s+content="([^"]+)"\s*\/?>/i);
  if (dateMatch && dateMatch[1]) {
    metadata.date = dateMatch[1];
  }
  
  const categoryMatch = html.match(/<meta\s+name="category"\s+content="([^"]+)"\s*\/?>/i);
  if (categoryMatch && categoryMatch[1]) {
    metadata.category = categoryMatch[1];
  }
  
  // Note: HTML meta tags don't support arrays well, so subcategories are only loaded from metadata.json
  // const subCategoryMatch = html.match(/<meta\s+name="subcategory"\s+content="([^"]+)"\s*\/?>/i);
  // if (subCategoryMatch && subCategoryMatch[1]) {
  //   metadata.subCategory = subCategoryMatch[1];
  // }
  
  const excerptMatch = html.match(/<meta\s+name="excerpt"\s+content="([^"]+)"\s*\/?>/i);
  if (excerptMatch && excerptMatch[1]) {
    metadata.excerpt = excerptMatch[1];
  }
  
  const thumbnailMatch = html.match(/<meta\s+name="thumbnail"\s+content="([^"]+)"\s*\/?>/i);
  if (thumbnailMatch && thumbnailMatch[1]) {
    metadata.thumbnail = thumbnailMatch[1];
  }
  
  const tagsMatch = html.match(/<meta\s+name="tags"\s+content="([^"]+)"\s*\/?>/i);
  if (tagsMatch && tagsMatch[1]) {
    metadata.tags = tagsMatch[1].split(',').map(tag => tag.trim());
  }
  
  // IDを生成（スラッグをベースに）
  if (metadata.slug) {
    metadata.id = `article-${metadata.slug}`;
  }
  
  return metadata;
};

// 画像URLを生成する関数
export const getImageUrl = (articleSlug: string, imagePath: string, articlePath?: string, size?: string): string => {
  if (!articleSlug) {
    return ''; // articleSlugがない場合は空文字列を返す
  }

  // パスを正規化
  let normalizedImagePath = imagePath;
  
  // 既に完全なURLの場合はそのまま返す
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // 既に/articlesを含むパスの場合はそのまま返す
  if (imagePath.startsWith('/articles/')) {
    return imagePath;
  }
  
  // パスの正規化
  normalizedImagePath = imagePath.replace(/^\/+/, ''); // 先頭のスラッシュを削除
  
  // 記事パスからディレクトリIDを抽出（存在する場合）
  let directoryId = '';
  if (articlePath) {
    const directoryIdMatch = articlePath.match(/\/articles\/([^\/]+)\/main\.html/);
    directoryId = directoryIdMatch ? directoryIdMatch[1] : '';
  }
  
  // ディレクトリIDがない場合はスラッグを使用
  const articleId = directoryId || articleSlug;
  
  // 画像URLを構築（Nginxの設定に合わせたパス）
  return `${API_BASE_URL}/articles/${articleId}/${normalizedImagePath}`;
};

// メタデータから記事情報を生成する関数（記事本文を読み込まない軽量版）
export const createArticleFromMetadata = (metadata: any): Article => {
  // HTMLタグを除去する関数
  const stripHtmlTags = (html: string) => {
    return html.replace(/<[^>]*>/g, '');
  };
  
  // ディレクトリIDを取得
  const directoryId = metadata.directory || metadata.id;
  
  // サムネイル画像のパスを設定
  let thumbnail = '';
  if (directoryId) {
    // メタデータからカバー画像の有無を確認
    if (metadata.has_cover_image) {
      // カバー画像のパスを構築
      thumbnail = `/articles/${directoryId}/cover.webp`;
    } else {
      // デフォルト画像を使用
      thumbnail = '/images/arayottokun.svg';
    }
  } else {
    thumbnail = '/images/arayottokun.svg';
  }
  
  // タグから国名を取得（すべてのタグは国名）
  const countryNames = metadata.tags?.map((tag: any) => tag.name) || [];
  
  return {
    id: metadata.id,
    title: stripHtmlTags(metadata.title || ''),
    slug: metadata.slug || metadata.id,
    date: metadata.created_at || new Date().toISOString().split('T')[0],
    created_at: metadata.created_at || new Date().toISOString().split('T')[0],
    category: metadata.category || 'その他',
    subCategory: Array.isArray(metadata.subcategory) ? metadata.subcategory : [],
    content: '', // 記事一覧表示では不要
    excerpt: metadata.excerpt || '',
    thumbnail,
    sourcePath: metadata.file_path || '',
    tags: countryNames,
    is_top: metadata.is_top || false
  };
};

// 記事ディレクトリからすべての記事を読み込む関数
export const findAndLoadArticlesFromDirectory = async (directoryPath: string): Promise<Article[]> => {
  try {
    // メタデータを取得
    const metadataResponse = await fetch(METADATA_URL, {
      headers: {
        'Cache-Control': 'no-cache'
      },
      credentials: 'same-origin',
      mode: 'cors'
    });
    
    if (!metadataResponse.ok) {
      throw new Error(`Failed to fetch metadata (${metadataResponse.status}): ${metadataResponse.statusText}`);
    }
    
    const metadata = await metadataResponse.json();
        
    // すべての記事を読み込んで結合
    const allArticles: Article[] = [];
    
    // メタデータから記事情報を生成（記事本文を読み込まない）
    for (const item of metadata) {
      try {
        if (!item.slug) {
          continue;
        }
        
        // メタデータから記事情報を生成
        const articleData = createArticleFromMetadata(item);
        allArticles.push(articleData);
      } catch (error: any) {
        console.error(`Failed to create article from metadata: ${error?.message || String(error)}`);
      }
    }
            
    return allArticles;
  } catch (error: any) {
    throw new Error(`Failed to load articles from directory: ${error?.message || String(error)}`);
  }
};

// 単一の記事を読み込む関数
export const loadArticleFromPath = async (articlePath: string, articleSlug: string, metadataItem?: any): Promise<Article | null> => {
  
  try {
    // URLの形式を確認・修正
    const fullArticleUrl = articlePath.startsWith('http') 
      ? articlePath 
      : articlePath.startsWith('/') 
        ? `${API_BASE_URL}${articlePath}` 
        : `${API_BASE_URL}/${articlePath}`;
        
    // 記事データを取得
    const response = await fetch(fullArticleUrl, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache'
      },
      credentials: 'same-origin',
      mode: 'cors'
    });
    
    if (!response.ok) {
      throw new Error(`記事の読み込みに失敗しました (${response.status})`);
    }
    
    // レスポンスが空でないか確認
    const contentLength = response.headers.get('Content-Length');
    if (contentLength && parseInt(contentLength) === 0) {
      throw new Error('Received empty response');
    }
    
    const htmlResponse = await response.text();
    
    // メタデータを抽出
    const metadata = extractMetadata(htmlResponse);
    
    if (!metadata.title || !metadata.slug) {
      // ファイル名から記事IDを抽出（必要に応じて）
      const idMatch = articleSlug.match(/^([^_]+)/);
      const extractedId = idMatch ? idMatch[1] : null;
      
      if (!metadata.title) metadata.title = `記事 ${extractedId || 'Unknown'}`;
      if (!metadata.slug) metadata.slug = extractedId || `article-${Date.now()}`;  
    }
    
    // 記事本文を抽出（メインコンテンツのdivから）
    const contentMatch = htmlResponse.match(/<div\s+id="content"[^>]*>([\s\S]*?)<\/div>/i);
    const rawContent = contentMatch ? contentMatch[1] : htmlResponse;
    
    // 画像パスを先に変換しておく
    const contentWithFixedImagePaths = rawContent.replace(
      /(<img[^>]+src=")([^"]+)(")/gi,
      (match, prefix, imgSrc, suffix) => {
        // すでに完全なURLの場合はそのまま
        if (imgSrc.startsWith('http://') || imgSrc.startsWith('https://') || imgSrc.startsWith('/articles/')) {
          return match;
        }
        
        // 記事パスからディレクトリIDを抽出
        const directoryIdMatch = articlePath.match(/\/articles\/([^\/]+)\/main\.html/);
        const directoryId = directoryIdMatch ? directoryIdMatch[1] : '';
        
        // 相対パスの画像URLを作成
        const imageUrl = getImageUrl(metadata.slug || articleSlug, imgSrc, articlePath);
        return `${prefix}${imageUrl}${suffix}`;
      }
    );
    
    // フォントスタイルを追加
    const contentWithFontStyle = `<style>
      .article-content * {
        font-family: 'Zen Maru Gothic', sans-serif;
      }
    </style>
    <div class="article-content">
      ${contentWithFixedImagePaths}
    </div>`;
    
    // 既存のfigure/figcaptionタグを保持しながら、単独の画像にのみキャプションを追加
    // まず、figure/figcaptionで囲まれた画像を一時的なプレースホルダーに置き換え
    const placeholders = new Map();
    let placeholderIndex = 0;
    
    // より柔軟なパターンでfigureタグを検出
    const contentWithPlaceholders = contentWithFontStyle.replace(
      /<figure[^>]*>[\s\S]*?<img[\s\S]*?<figcaption[\s\S]*?<\/figcaption>[\s\S]*?<\/figure>/gi,
      (match) => {
        const placeholder = `__FIGURE_PLACEHOLDER_${placeholderIndex}__`;
        placeholders.set(placeholder, match);
        placeholderIndex++;
        return placeholder;
      }
    );
    
    // 残りの単独の画像タグに対してキャプション処理を適用
    const processedContent = contentWithPlaceholders.replace(
      /<img[^>]*(?:alt="[^"]*")?[^>]*>/gi,
      (match) => {          
        // alt属性を抽出
        const altMatch = match.match(/alt="([^"]*)"/i);
        const alt = altMatch ? altMatch[1].trim() : '';
        
        // alt属性を削除した画像タグを作成
        const imgWithoutAlt = match.replace(/alt="[^"]*"/gi, '');

        // カバー画像を削除
        if (match.includes('src="cover.webp"') || 
            match.includes('class="cover-image"') || 
            (alt === 'カバー画像' && match.includes('cover.'))) {
          return '';
        }
        
        // カバー画像の場合はキャプションを表示しない
        if (match.includes('cover.') || match.toLowerCase().includes('カバー画像')) {
          return imgWithoutAlt;
        }
        
        // altが"画像"または"カバー画像"の場合はキャプションを表示しない
        if (alt === '画像' || alt === 'カバー画像') {
          return imgWithoutAlt;
        }
        
        // キャプションがある場合のみfigure要素でラップ
        if (alt) {
          return `<figure class="article-image">
            ${imgWithoutAlt}
            <figcaption class="image-caption">${alt}</figcaption>
          </figure>`;
        }
        
        // キャプションがない場合は画像のみを返す（alt属性なし）
        return imgWithoutAlt;
      }
    );
    
    // プレースホルダーを元のfigure/figcaptionタグに戻す
    const finalContent = processedContent.replace(
      /__FIGURE_PLACEHOLDER_\d+__/g,
      (placeholder) => {
        const originalTag = placeholders.get(placeholder) || placeholder;
        
        // 既存のfigureタグにクラスを追加
        const figureWithClass = originalTag.replace(
          /<figure([^>]*)>/i,
          (match: string, attributes: string) => {
            // すでにclass属性がある場合は追加、ない場合は新規作成
            if (attributes.includes('class=')) {
              return match.replace(/class="([^"]*)"/i, (classMatch: string, classValue: string) => {
                if (!classValue.includes('article-image')) {
                  return `class="${classValue} article-image"`;
                }
                return classMatch;
              });
            } else {
              return `<figure class="article-image"${attributes}>`;
            }
          }
        );
        
        // 既存のfigcaptionタグにクラスを追加
        const figureAndCaptionWithClass = figureWithClass.replace(
          /<figcaption([^>]*)>/i,
          (match: string, attributes: string) => {
            // すでにclass属性がある場合は追加、ない場合は新規作成
            if (attributes.includes('class=')) {
              return match.replace(/class="([^"]*)"/i, (classMatch: string, classValue: string) => {
                if (!classValue.includes('image-caption')) {
                  return `class="${classValue} image-caption"`;
                }
                return classMatch;
              });
            } else {
              return `<figcaption class="image-caption"${attributes}>`;
            }
          }
        );
        
        return figureAndCaptionWithClass;
      }
    );
    
    const processedContentWithImages = finalContent;
    
    // サムネイル画像のパスを設定
    let thumbnail = '';
    try {
      // 記事パスからディレクトリIDを抽出
      const directoryIdMatch = articlePath.match(/\/articles\/([^\/]+)\/main\.html/);
      const directoryId = directoryIdMatch ? directoryIdMatch[1] : '';
      
      if (!directoryId) {
        thumbnail = '/images/arayottokun.svg';
      } else {
        // メタデータからカバー画像の有無を確認
        const hasCoverImage = metadataItem ? metadataItem.has_cover_image : false;
        
        if (hasCoverImage) {
          // カバー画像のパスを構築
          thumbnail = `/articles/${directoryId}/cover.webp`;
        } else {
          // メタデータがない場合やカバー画像がない場合は実際に確認
          const coverImagePath = `/articles/${directoryId}/cover.webp`;
          
          try {
            // カバー画像の存在確認
            const coverResponse = await fetch(coverImagePath, { method: 'HEAD' });
            if (coverResponse.ok) {
              thumbnail = coverImagePath;
            } else {
              thumbnail = '/images/arayottokun.svg';
            }
          } catch (error) {
            thumbnail = '/images/arayottokun.svg';
          }
        }
      }
    } catch (error) {
      thumbnail = '/images/arayottokun.svg';
    }
    
    return {
      id: metadata.id || `article-${metadata.slug}`,
      title: metadata.title || `記事 ${metadata.id || metadata.slug}`,
      slug: metadata.slug || articleSlug,
      date: metadata.date || new Date().toISOString().split('T')[0],
      created_at: metadataItem?.created_at || new Date().toISOString().split('T')[0],
      category: metadata.category || 'その他',
      subCategory: metadata.subCategory || [],
      content: processedContentWithImages,
      excerpt: metadata.excerpt || '',
      thumbnail,
      sourcePath: articlePath,
      tags: metadata.tags || [],
      is_top: metadata.is_top || false
    };
  } catch (error: any) {
    throw error;
  }
}; 